import { useQuery } from "@tanstack/react-query"
import { FolderTree, Layers, PiggyBank, TriangleAlert } from "lucide-react"
import { useRef, useState } from "react"
import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { TarjetaKpi } from "@/componentes/ui/TarjetaKpi"

import { CategoriasGasto } from "@/paginas/interno/CategoriasGasto"
import { type CategoriaGasto, type FiltroGasto, Gastos } from "@/paginas/interno/Gastos"

type GastoResumen = {
  id: number
  categoria_id: number | null
  tipo_gasto: "FIJO" | "VARIABLE"
  monto_referencia: string | null
  activo: boolean
}

const NOMBRE_FILTRO: Record<NonNullable<FiltroGasto>, string> = {
  fijos: "sólo los fijos",
  variables: "sólo los variables",
  "sin-categoria": "sólo los que no tienen categoría",
  archivados: "sólo los archivados",
}

/**
 * Gastos y categorías: UNA vista, porque son una sola cosa.
 *
 * ⚠ **Eran dos pestañas y se juntaron.** Un gasto sin categoría no se puede agrupar en
 * ningún informe, y una categoría sin gastos no agrupa nada: la pregunta «¿está esto
 * bien montado?» se contesta mirando las dos listas a la vez, y con una pestaña de por
 * medio había que recordar la otra. Las categorías van a la derecha porque son el
 * agrupador —pocas, estables—, y los gastos ocupan el ancho porque son lo que se toca.
 *
 * ⚠ **Las cifras se cuentan en el navegador, y aquí sí vale.** Esto es un CATÁLOGO, no
 * un ledger: son decenas de filas y ya están las dos listas en memoria —con las mismas
 * claves de consulta que usan las tablas, así que no se pide nada dos veces—. La regla
 * de «la paginación es del servidor» sigue valiendo para lo que crece sin parar; un
 * catálogo que pase de 100 gastos tendría que mover estas cuentas al servidor, como
 * hizo la bandeja de órdenes.
 *
 * ⚠ **El hueco que importa es «sin categoría».** No es un aviso de limpieza: un gasto
 * así desaparece del informe por categoría sin que nada lo diga, y el total sigue
 * cuadrando — que es la peor forma de estar mal.
 */
export function GastosYCategorias() {
  // ⚠ **Cada tarjeta es un filtro de la tabla de abajo**, como en la bandeja de
  // órdenes: una cifra que no se puede pulsar obliga a recorrer la lista a ojo para
  // ver cuáles son, y entonces deja más dudas de las que aclara. Pulsar otra vez lo
  // quita.
  const [filtro, setFiltro] = useState<FiltroGasto>(null)
  const vista = useRef<HTMLDivElement | null>(null)
  const alternar = (cual: FiltroGasto) => setFiltro(filtro === cual ? null : cual)

  const gastos = useQuery({
    queryKey: ["gastos"],
    queryFn: () => api.get<GastoResumen[]>("/api/v1/gastos?limit=100"),
  })
  const categorias = useQuery({
    queryKey: ["categorias-gasto"],
    queryFn: () => api.get<CategoriaGasto[]>("/api/v1/categorias-gasto"),
  })

  const filas = gastos.data ?? []
  const activos = filas.filter((g) => g.activo)
  const fijos = activos.filter((g) => g.tipo_gasto === "FIJO").length
  const sinCategoria = activos.filter((g) => g.categoria_id === null).length
  const sinReferencia = activos.filter((g) => g.monto_referencia === null).length

  const grupos = categorias.data ?? []
  const activas = grupos.filter((c) => c.activo)
  const usadas = new Set(activos.map((g) => g.categoria_id).filter((id) => id !== null))
  const vacias = activas.filter((c) => !usadas.has(c.id)).length

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Exportar
          titulo="Gastos y categorías"
          subtitulo={filtro !== null ? `Gastos: ${NOMBRE_FILTRO[filtro]}` : undefined}
          contenido={vista}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <TarjetaKpi
          titulo="Gastos en el catálogo"
          detalle="Las plantillas desde las que se redacta una orden"
          Icono={PiggyBank}
          color="var(--info)"
          cantidad={activos.length}
          pie={
            filas.length > activos.length
              ? `${filas.length - activos.length} archivados · pulsa para verlos`
              : "Ninguno archivado"
          }
          activa={filtro === "archivados"}
          onClick={() => alternar("archivados")}
        />
        <TarjetaKpi
          titulo="Fijos y variables"
          detalle="Lo que vuelve cada mes frente a lo que depende"
          Icono={Layers}
          color="var(--warning)"
          cantidad={
            <>
              {fijos}
              <span className="text-base font-normal" style={{ color: "var(--fg-muted)" }}>
                {" "}
                / {activos.length - fijos}
              </span>
            </>
          }
          pie={
            filtro === "fijos"
              ? "Viendo los fijos · pulsa para quitar el filtro"
              : "Fijos / variables. Pulsa para ver los fijos."
          }
          activa={filtro === "fijos"}
          onClick={() => alternar("fijos")}
        />
        <TarjetaKpi
          titulo="Categorías activas"
          detalle="El agrupador de todos los informes"
          Icono={FolderTree}
          color="var(--success)"
          cantidad={activas.length}
          pie={vacias > 0 ? `${vacias} todavía sin ningún gasto` : "Todas en uso"}
        />
        <TarjetaKpi
          titulo="Huecos"
          detalle="Lo que dejaría un informe incompleto"
          Icono={TriangleAlert}
          color={sinCategoria > 0 ? "var(--destructive)" : "var(--fg-muted)"}
          cantidad={sinCategoria}
          pie={
            sinCategoria > 0
              ? "Sin categoría: no salen en el informe. Pulsa para verlos."
              : sinReferencia > 0
                ? `Ninguno sin categoría. ${sinReferencia} sin monto de referencia.`
                : "Ninguno sin categoría ni sin monto."
          }
          activa={filtro === "sin-categoria"}
          onClick={sinCategoria > 0 ? () => alternar("sin-categoria") : undefined}
        />
      </div>

      <div ref={vista} className="grid gap-4 lg:grid-cols-3">
        <section className="lg:col-span-2">
          <h2 className="mb-2 flex flex-wrap items-baseline gap-2 text-sm font-semibold">
            En qué gasta la agencia
            {filtro !== null && (
              <button
                type="button"
                onClick={() => setFiltro(null)}
                className="foco-cristal text-xs font-normal underline-offset-2 hover:underline"
                style={{ color: "var(--primary-texto)" }}
              >
                Quitar el filtro
              </button>
            )}
          </h2>
          <Gastos filtro={filtro} />
        </section>
        <section>
          <h2 className="mb-2 text-sm font-semibold">Cómo se agrupa</h2>
          <CategoriasGasto />
        </section>
      </div>
    </div>
  )
}
